import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import fetch from 'isomorphic-unfetch';
import App from 'next/app';
import config from '.';

type TApolloClient = ApolloClient<NormalizedCacheObject>;

type InitialProps = {
  apolloClient: TApolloClient;
  apolloState: any;
} & Record<string, any>;

let globalApolloClient: TApolloClient;

/**
 * Creates and configures the ApolloClient
 * @param  {Object} [initialState={}]
 */
function createApolloClient(initialState = {}, cookie?: string) {
  const enhancedFetch = (
    input: RequestInfo | URL,
    init?: RequestInit | undefined,
  ) => {
    return fetch(input, {
      ...init,
      headers: {
        ...init?.headers,
        Cookie: cookie || '',
      },
    }).then(response => {
      return response;
    });
  };

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    ssrMode: typeof window === 'undefined', // Disables forceFetch on the server (so queries are only run once)
    link: new HttpLink({
      uri: `${config.api.flight}/graphql`, // Server URL (must be absolute)
      fetch: enhancedFetch,
      credentials: 'include',
    }),
    cache: new InMemoryCache({
      typePolicies: {
        Booking: { keyFields: ['bookingKey', 'otaId'] },
        PriceChangesSubscription: { keyFields: ['searchKey'] },
        PriceTransparencyDetail: { keyFields: ['id', 'otaId'] },
        PromotionPaypoint: { keyFields: ['agtCode', 'bookingKey'] },
        TravelClubPaypoint: { keyFields: ['agtCode', 'bookingId'] },
        Favorite: { keyFields: ['flightId'] },
      },
    }).restore(initialState),
  });
}

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {NormalizedCacheObject} initialState
 */
export function initApolloClient(initialState?: any, cookie?: string) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, cookie);
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, document.cookie);
  }

  return globalApolloClient;
}

/**
 * Creates and provides the apolloContext
 * to a next.js AppTree. Use it by wrapping
 * your AppComponent via HOC pattern.
 * @param {Function|Class} AppComponent
 * @param {Object} [config]
 * @param {Boolean} [config.ssr=true]
 */
export function withApollo(AppComponent: any, { ssr = true } = {}) {
  const WithApollo = ({
    apolloClient,
    apolloState,
    ...pageProps
  }: InitialProps) => {
    const client = apolloClient || initApolloClient(apolloState);
    return (
      <ApolloProvider client={client}>
        <AppComponent {...pageProps} />
      </ApolloProvider>
    );
  };

  // Set the correct displayName in development
  if (process.env.NODE_ENV !== 'production') {
    const displayName =
      AppComponent.displayName || AppComponent.name || 'Component';
    WithApollo.displayName = `withApollo(${displayName})`;
  }

  if (ssr || AppComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx: any) => {
      const { AppTree } = ctx;

      // Initialize ApolloClient
      const apolloClient = initApolloClient(
        {},
        ctx.ctx.req?.headers.cookie || '',
      );

      // Add apolloClient to NextPageContext & NextAppContext.
      // This allows us to consume the apolloClient inside our
      // custom `getInitialProps({ apolloClient })`.
      ctx.apolloClient = apolloClient;
      ctx.ctx.apolloClient = apolloClient;

      // Run wrapped getInitialProps methods
      let pageProps = {};
      if (AppComponent.getInitialProps) {
        pageProps = await AppComponent.getInitialProps(ctx);
      } else {
        pageProps = await App.getInitialProps(ctx);
      }

      // Only on the server:
      if (typeof window === 'undefined') {
        // When redirecting, the response is finished.
        // No point in continuing to render
        if (ctx.ctx.res && ctx.ctx.res.finished) {
          return pageProps;
        }

        // Only if ssr is enabled
        if (ssr) {
          try {
            // Run all GraphQL queries
            const { getDataFromTree } = await import(
              '@apollo/client/react/ssr'
            );
            await getDataFromTree(
              <AppTree {...pageProps} apolloClient={apolloClient} />,
            );
          } catch (error) {
            // Prevent Apollo Client GraphQL errors from crashing SSR.
            // Handle them in components via the data.error prop:
            // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
            console.error('Error while running `getDataFromTree`', error);
          }
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apolloClient.cache.extract();

      return {
        ...pageProps,
        apolloState,
      };
    };
  }

  return WithApollo;
}
